import {useState, SetStateAction, Dispatch} from 'react'
import {RIF, t, _} from '../../lib'
import {selectTheme} from '../../store'
import {
  DragHandle,
  QuestionnaireInput,
  QuestionnaireTextarea,
  QuestionnaireInputHeight,
  QuestionnaireDropdown,
  QuestionnaireInputInitialValue,
} from '..'
import {CSS} from '@dnd-kit/utilities'
import {ContentListItem, ContentListItemType, QuestionnaireTempSection} from '../../model'
import {useSortable} from '@dnd-kit/sortable'

export const QuestionnaireSection = (props: {
  key: string
  id: string
  index: number
  section: QuestionnaireTempSection
  isEditing: boolean
  setEditingBlock: Dispatch<SetStateAction<string | null>>
  addSection: (index: number) => void
  addQuestion: (index: number) => void
  duplicateItem: (index: number) => void
  deleteItem: (index: number) => void
  viewOnly: boolean
  questionnaireContentList: ContentListItem[]
  setQuestionnaireContentList: Dispatch<SetStateAction<ContentListItem[]>>
}) => {
  const {pad, color, fontWeight, fontSize} = selectTheme()
  const {
    id,
    index,
    section,
    isEditing,
    setEditingBlock,
    addSection,
    addQuestion,
    duplicateItem,
    deleteItem,
    viewOnly,
    questionnaireContentList,
    setQuestionnaireContentList,
  } = props
  const {attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef} = useSortable({id: props.id})
  const style = {transform: CSS.Translate.toString(transform)}
  const [hovering, setHovering] = useState(false)
  const sectionIndex =
    questionnaireContentList.filter((c) => c.type === ContentListItemType.Section).findIndex((s) => s.id === id) + 1
  const handleChangeSectionLegend = (value: string) => {
    const newContentList = [...questionnaireContentList]
    newContentList.splice(index, 1, {id, type: ContentListItemType.Section, item: {...section, legend: value}})
    setQuestionnaireContentList(newContentList)
  }

  const handleChangeSectionDescription = (value: string) => {
    const newContentList = [...questionnaireContentList]
    newContentList.splice(index, 1, {
      id,
      type: ContentListItemType.Section,
      item: {...section, description: value ?? ''},
    })
    setQuestionnaireContentList(newContentList)
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingBlock(id)
  }

  const handleClickAddNewQuestion = (e: React.MouseEvent) => {
    e.stopPropagation()
    addQuestion(index)
  }

  const handleClickAddNewSection = (e: React.MouseEvent) => {
    e.stopPropagation()
    addSection(index)
  }

  const handleMouseEnter = () => {
    setHovering(true)
  }

  const handleMouseLeave = () => {
    setHovering(false)
  }
  const showDragHandle = !viewOnly && (isEditing || hovering)
  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={setNodeRef}
      {...attributes}
      style={{
        ...style,
        marginLeft: '-24px',
        paddingLeft: '24px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        cursor: 'pointer',
      }}>
      {/* drag handle */}
      {RIF(showDragHandle, <DragHandle ref={setActivatorNodeRef} {...listeners} />)}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          width: 'calc(100% - 24px)',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px',
        }}>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}>
          {/* section index tag */}
          <span
            css={{
              whiteSpace: 'nowrap',
              width: '85px',
              minWidth: '85px',
              maxWidth: '85px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: color.border._600,
              borderRadius: '8px 8px 0 0',
              color: color.textIcon.white,
              fontSize: fontSize.h7,
              fontWeight: fontWeight.thick,
            }}>
            Section {sectionIndex}
          </span>
          <div
            onClick={handleClick}
            css={{
              width: '100%',
              borderRadius: `0 ${isEditing ? '5px' : '0'} 5px 5px`,
              border: isEditing ? `1px solid ${color.border.blue}` : 'none',
              borderTop: isEditing ? `1px solid ${color.border.blue}` : `1px solid ${color.border._600}`,
              padding: `16px ${isEditing ? '16px' : '24px'}`,
              background: color.white,
              boxShadow: '0px 4px 12px rgba(213, 213, 213, 0.25)',
              cursor: viewOnly ? 'auto' : 'pointer',
              ':hover': {
                background: viewOnly ? color.white : color.grey_20,
              },
            }}>
            {RIF(
              !isEditing,
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}>
                <p css={{fontWeight: fontWeight.bold, fontSize: fontSize.h3}}>{section?.legend}</p>
                <p>{section?.description || QuestionnaireInputInitialValue.SectionDescription}</p>
              </div>,
            )}
            {/* show drop down menu when hovered */}
            {RIF(
              !isEditing && showDragHandle,
              <div
                css={{
                  position: 'absolute',
                  top: '48px',
                  right: '16px',
                }}>
                <QuestionnaireDropdown
                  {...{
                    index,
                    deleteItem,
                    duplicateItem,
                  }}
                />
              </div>,
            )}
            {RIF(
              isEditing,
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                  <QuestionnaireInput
                    placeholder={QuestionnaireInputInitialValue.SectionTitle}
                    onChangeAction={handleChangeSectionLegend}
                    defaultValue={section.legend}
                    maxLength={50}
                    questionnaireInputHeight={QuestionnaireInputHeight.big}
                  />
                  <QuestionnaireDropdown
                    {...{
                      index,
                      deleteItem,
                      duplicateItem,
                    }}
                  />
                </div>
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                  <QuestionnaireTextarea
                    defaultValue={section?.description}
                    onChangeAction={handleChangeSectionDescription}
                    placeholder={QuestionnaireInputInitialValue.SectionDescription}
                    maxLength={250}
                    rows={4}
                  />
                  <div css={{width: '32px', minWidth: '32px'}} />
                </div>
              </div>,
            )}
          </div>
        </div>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: '30px',
            height: '18px',
          }}>
          {RIF(
            isEditing || showDragHandle,
            <>
              <button
                onClick={handleClickAddNewQuestion}
                css={{
                  fontWeight: fontWeight.thick,
                  color: color.textIcon.link,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                }}>
                + New Question
              </button>
              <button
                onClick={handleClickAddNewSection}
                css={{
                  fontWeight: fontWeight.thick,
                  color: color.textIcon.link,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                }}>
                + New Section
              </button>
            </>,
          )}
        </div>
      </div>
    </div>
  )
}
